import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Bell, Check, X, AlertTriangle } from 'lucide-react';

const SEV_STYLE = {
  info: 'border-l-blue-500 bg-blue-50',
  warning: 'border-l-amber-500 bg-amber-50',
  critical: 'border-l-red-500 bg-red-50'
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const ref = useRef(null);

  const fetchData = async () => {
    try {
      const r = await axios.get(`${API}/notifications`, { withCredentials: true });
      setItems(r.data);
      setUnread(r.data.filter(n => !n.is_read).length);
    } catch { /* silent */ }
  };

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 30000);  // poll every 30s
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const markRead = async (id) => {
    try { await axios.put(`${API}/notifications/${id}/read`, {}, { withCredentials: true }); fetchData(); } catch {}
  };

  const markAll = async () => {
    try { await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true }); fetchData(); } catch {}
  };

  const click = (n) => {
    markRead(n.id);
    if (n.link) navigate(n.link);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button data-testid="notif-bell-btn" onClick={() => setOpen(!open)} className="relative p-2 hover:bg-slate-100 rounded-lg transition">
        <Bell className="w-5 h-5 text-slate-700" />
        {unread > 0 && (
          <span data-testid="notif-unread-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{unread > 9 ? '9+' : unread}</span>
        )}
      </button>
      {open && (
        <div data-testid="notif-dropdown" className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-slate-200 max-h-[80vh] overflow-y-auto z-50">
          <div className="flex items-center justify-between p-3 border-b border-slate-100">
            <h3 className="font-semibold text-foreground">Notifications</h3>
            {unread > 0 && (
              <button data-testid="mark-all-read-btn" onClick={markAll} className="text-xs text-teal-600 hover:underline">Mark all read</button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No notifications</div>
          ) : (
            <div className="divide-y divide-slate-50">
              {items.map(n => (
                <button
                  key={n.id}
                  data-testid={`notif-item-${n.id}`}
                  onClick={() => click(n)}
                  className={`w-full p-3 hover:bg-slate-50 text-left flex gap-2 border-l-4 transition ${SEV_STYLE[n.severity] || ''} ${!n.is_read ? '' : 'opacity-60'}`}
                >
                  <div className="flex-shrink-0">
                    {n.severity === 'critical' || n.severity === 'warning' ? <AlertTriangle className="w-4 h-4 text-amber-500" /> : <Bell className="w-4 h-4 text-blue-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-foreground truncate">{n.title}</div>
                    <div className="text-xs text-slate-600 line-clamp-2">{n.body}</div>
                    <div className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                  {!n.is_read && <div className="w-2 h-2 rounded-full bg-teal-500 mt-1.5" />}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
